import { device, colors, getTypographyFontStyle } from "../../styles/global.style";
import styled from "styled-components";

const ContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 90px;
  padding: 0 16px 60px;

  @media only screen and ${device.md} {
    margin-top: 120px;
    padding: 0 24px 80px;
  }

  @media ${device.lg} {
    padding: 0 48px 100px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
  gap: 12px;
  text-align: center;

  @media ${device.md} {
    margin-bottom: 48px;
  }

  .subtitle {
    ${getTypographyFontStyle({ variant: "body1", weight: 400 })};
    color: ${colors.darkGrey};
    max-width: 500px;
    line-height: 1.6;
  }
`;

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  width: 100%;
  max-width: 1200px;

  @media ${device.lg} {
    grid-template-columns: 1fr 1.2fr;
    gap: 48px;
    align-items: start;
  }
`;

const ContactInfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media ${device.md} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.lg} {
    grid-template-columns: 1fr;
    position: sticky;
    top: 140px;
  }
`;

const ContactInfoCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${colors.border};
  transition: all 0.3s ease;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  &:hover {
    border-color: ${colors.primary};
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(10, 22, 40, 0.08);
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${colors.primaryBg};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .icon {
      font-size: 24px;
      line-height: 1;
    }
  }

  .content {
    flex: 1;
  }

  .label {
    ${getTypographyFontStyle({ variant: "body2", weight: 600 })};
    color: ${colors.secondary};
    margin-bottom: 6px;
  }

  .value {
    ${getTypographyFontStyle({ variant: "small", weight: 400 })};
    color: ${colors.darkGrey};
    line-height: 1.6;

    a {
      color: ${colors.darkGrey};
      text-decoration: none;
      transition: color 0.3s ease;

      &:hover {
        color: ${colors.primary};
      }
    }
  }

  @media ${device.md} {
    padding: 20px;
  }
`;

const FormContainer = styled.div`
  background: ${colors.white};
  border-radius: 24px;
  padding: 32px 24px;
  border: 1px solid ${colors.border};
  box-shadow: 0 4px 24px rgba(10, 22, 40, 0.04);

  @media ${device.md} {
    padding: 40px 32px;
  }

  @media ${device.lg} {
    padding: 48px 40px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 24px;

  @media ${device.md} {
    margin-bottom: 32px;
  }

  h3 {
    ${getTypographyFontStyle({ variant: "h3", weight: 600 })};
    color: ${colors.secondary};
    margin-bottom: 8px;
  }

  p {
    ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
    color: ${colors.darkGrey};
    margin: 0;
  }
`;

const InputContainer = styled.div`
  margin-bottom: 20px;

  @media ${device.md} {
    margin-bottom: 24px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 8px;

  & > button {
    width: 100%;
    padding: 16px 32px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 16px;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(245, 166, 35, 0.35);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  @media ${device.md} {
    margin-top: 8px;
  }
`;

const RequiredNote = styled.p`
  ${getTypographyFontStyle({ variant: "small", weight: 400 })};
  color: ${colors.darkGrey};
  margin: -8px 0 20px;
  font-style: italic;

  .asterisk {
    color: ${colors.primary};
    font-weight: 600;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, ${colors.accentGreen}15 0%, ${colors.accentGreen}08 100%);
  border: 1px solid ${colors.accentGreen}30;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;

  .success-icon {
    width: 64px;
    height: 64px;
    background: ${colors.accentGreen};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 32px;
    color: white;
  }

  h3 {
    ${getTypographyFontStyle({ variant: "h3", weight: 600 })};
    color: ${colors.secondary};
    margin-bottom: 8px;
  }

  p {
    ${getTypographyFontStyle({ variant: "body1", weight: 400 })};
    color: ${colors.darkGrey};
    margin: 0;
  }
`;

export {
  ContainerWrapper,
  HeaderContainer,
  ContactLayout,
  ContactInfoSection,
  ContactInfoCard,
  FormContainer,
  FormHeader,
  InputContainer,
  ButtonWrapper,
  RequiredNote,
  SuccessMessage,
};
